import { useEffect, useRef, useState, useCallback } from "react";
import { MessageSquare, Tag, Reply, Flag, Paperclip, X, ZoomIn, Trash2 } from "lucide-react";
import DOMPurify from "dompurify";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { usePostTenderMessage, useMarkTenderMessage, useDeleteTenderMessage } from "../hooks/useTenders";
import { uploadTenderMessageImage } from "../api/tenders";
import {
  MESSAGE_MARK_LABELS,
  type TenderMessage,
  type TenderMessageAttachment,
  type TenderMessageMarkType,
} from "../types";

const MARK_TYPE_OPTIONS: Array<{ value: TenderMessageMarkType; label: string }> = [
  { value: "important", label: "Ważne" },
  { value: "question", label: "Pytanie" },
  { value: "action", label: "Do działania" },
  { value: "decision", label: "Decyzja" },
];

const BADGE_COLORS: Record<TenderMessageMarkType, "red" | "blue" | "orange" | "purple"> = {
  important: "red",
  question: "blue",
  action: "orange",
  decision: "purple",
};

// Ids of every message in the thread, replies included — a mention can point at a reply.
function collectMessageIds(messages: TenderMessage[], acc = new Set<string>()) {
  for (const msg of messages) {
    acc.add(msg.id);
    if (msg.replies?.length) collectMessageIds(msg.replies, acc);
  }
  return acc;
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "a", "img"],
    ALLOWED_ATTR: ["start", "href", "target", "rel", "class", "src", "alt", "width", "height"],
  });
}

function MessageContent({ content, names }: { content: string; names: string[] }) {
  if (!content) return null;
  if (isHtmlContent(content)) {
    return (
      <div
        className="rich-text-content text-sm text-gray-700 dark:text-gray-300"
        dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
      />
    );
  }
  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

// ── Image lightbox ────────────────────────────────────────────────────────────
function ImageLightbox({ src, name, onClose }: { src: string; name: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between w-full mb-2">
          <span className="text-white text-sm">{name}</span>
          <button onClick={onClose} className="text-white hover:text-gray-300 p-1">
            <X size={20} />
          </button>
        </div>
        <img
          src={src}
          alt={name}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
        />
      </div>
    </div>
  );
}

// ── Attachment display ────────────────────────────────────────────────────────
function AttachmentList({ attachments }: { attachments: TenderMessageAttachment[] }) {
  const [lightbox, setLightbox] = useState<TenderMessageAttachment | null>(null);

  if (attachments.length === 0) return null;

  const images = attachments.filter((a) => a.is_image);
  const files = attachments.filter((a) => !a.is_image);

  return (
    <>
      {images.length > 0 && (
        <div className={`mt-2 grid gap-1 ${images.length === 1 ? "grid-cols-1 max-w-xs" : images.length === 2 ? "grid-cols-2 max-w-sm" : "grid-cols-3 max-w-md"}`}>
          {images.map((att) => (
            <button
              key={att.id}
              onClick={() => setLightbox(att)}
              className="relative group rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-video"
            >
              <img
                src={att.file_url ?? ""}
                alt={att.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="mt-1 space-y-1">
          {files.map((att) => (
            <a
              key={att.id}
              href={att.file_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
            >
              <Paperclip size={11} />
              {att.name}
            </a>
          ))}
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          src={lightbox.file_url ?? ""}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

// ── Pending file previews ─────────────────────────────────────────────────────
interface PendingFile {
  id: string;
  file: File;
  previewUrl?: string;
}

function PendingFilesList({ files, onRemove }: { files: PendingFile[]; onRemove: (id: string) => void }) {
  if (files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 px-1 py-1">
      {files.map((pf) => (
        <div key={pf.id} className="relative group">
          {pf.previewUrl ? (
            <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 ">
              <img src={pf.previewUrl} alt={pf.file.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-md flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 px-1">
              <Paperclip size={14} className="text-gray-400 mb-0.5" />
              <span className="text-[10px] text-gray-500 truncate w-full text-center leading-tight" title={pf.file.name}>
                {pf.file.name}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(pf.id)}
            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X size={9} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Single message item ───────────────────────────────────────────────────────
interface MessageItemProps {
  tenderId: string;
  message: TenderMessage;
  isReply?: boolean;
  users: { id: string; full_name: string }[];
  /** Message a mention notification pointed at — scrolled to and ringed. */
  highlightMessageId?: string | null;
}

function MessageItem({ tenderId, message, isReply = false, users, highlightMessageId }: MessageItemProps) {
  const user = useAuthStore((s) => s.user);
  const isHighlighted = !!highlightMessageId && highlightMessageId === message.id;
  const itemRef = useRef<HTMLDivElement>(null);

  // Bring the mentioned message into view. Replies are nested, so this fires on
  // whichever item matches, at any depth.
  useEffect(() => {
    if (isHighlighted) {
      itemRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isHighlighted]);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [replyMentionIds, setReplyMentionIds] = useState<string[]>([]);
  const [replyFiles, setReplyFiles] = useState<PendingFile[]>([]);
  const [markMenuOpen, setMarkMenuOpen] = useState(false);
  const replyFileRef = useRef<HTMLInputElement>(null);

  const postMessage = usePostTenderMessage(tenderId);
  const markMessage = useMarkTenderMessage(tenderId);
  const deleteMessage = useDeleteTenderMessage(tenderId);

  const mentionSuggestions = users.map((u) => ({ id: u.id, name: u.full_name }));
  const mentionNames = users.map((u) => u.full_name);

  const handleReply = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!replyContent.trim() && replyFiles.length === 0) return;
    await postMessage.mutateAsync({
      content: replyContent,
      parent: message.id,
      files: replyFiles.map((f) => f.file),
      mentionIds: replyMentionIds,
    });
    setReplyContent("");
    setReplyMentionIds([]);
    setReplyFiles([]);
    setReplyOpen(false);
  };

  const addReplyFiles = (newFiles: File[]) => {
    setReplyFiles((prev) => [
      ...prev,
      ...newFiles.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
      })),
    ]);
  };

  const handleMark = async (type: TenderMessageMarkType | null) => {
    await markMessage.mutateAsync({ messageId: message.id, is_marked: type !== null, mark_type: type });
    setMarkMenuOpen(false);
  };

  return (
    <div
      ref={itemRef}
      id={`tender-message-${message.id}`}
      className={`scroll-mt-24 ${isReply ? "ml-8 mt-2" : ""}`}
    >
      <div
        className={`p-3 rounded-lg ${
          message.is_marked
            ? "border-l-4 border-orange-400 bg-orange-50 dark:bg-orange-900/10"
            : "bg-gray-50 dark:bg-gray-700/50"
        } ${isHighlighted ? "ring-2 ring-[var(--color-primary)]" : ""}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {message.author_name}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {new Date(message.created_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
              </span>
              {message.is_marked && message.mark_type && (
                <Badge color={BADGE_COLORS[message.mark_type]}>
                  <Flag size={10} className="mr-1" />
                  {MESSAGE_MARK_LABELS[message.mark_type]}
                </Badge>
              )}
            </div>
            <MessageContent content={message.content} names={mentionNames} />
            <AttachmentList attachments={message.attachments} />
          </div>

          {user?.role !== "contractor" && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {!isReply && (
                <button
                  onClick={() => setReplyOpen((o) => !o)}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-gray-200"
                  title="Odpowiedz"
                >
                  <Reply size={14} />
                </button>
              )}
              <div className="relative">
                <button
                  onClick={() => setMarkMenuOpen((o) => !o)}
                  className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
                    message.is_marked ? "text-orange-500" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  }`}
                  title="Oznacz"
                >
                  <Tag size={14} />
                </button>
                {markMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg py-1 min-w-[140px]">
                    {message.is_marked && (
                      <button onClick={() => handleMark(null)} className="w-full text-left px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Usuń oznaczenie
                      </button>
                    )}
                    {MARK_TYPE_OPTIONS.map((opt) => (
                      <button key={opt.value} onClick={() => handleMark(opt.value)} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {(message.author === user?.id || user?.role === "director") && (
                <button
                  onClick={() => {
                    if (confirm("Usunąć tę wiadomość?")) deleteMessage.mutate(message.id);
                  }}
                  className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Usuń wiadomość"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {message.replies.map((reply) => (
        <MessageItem
          key={reply.id}
          tenderId={tenderId}
          message={reply}
          isReply
          users={users}
          highlightMessageId={highlightMessageId}
        />
      ))}

      {replyOpen && (
        <form onSubmit={handleReply} className="ml-8 mt-2 space-y-2">
          <PendingFilesList files={replyFiles} onRemove={(id) => setReplyFiles((p) => p.filter((f) => f.id !== id))} />
          <RichTextEditor
            value={replyContent}
            onChange={setReplyContent}
            onMentionIdsChange={setReplyMentionIds}
            onImageUpload={(file) => uploadTenderMessageImage(tenderId, file)}
            mentionSuggestions={mentionSuggestions}
            disabled={postMessage.isPending}
            placeholder="Odpowiedź…"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => replyFileRef.current?.click()}
              className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Dodaj plik"
            >
              <Paperclip size={15} />
            </button>
            <input ref={replyFileRef} type="file" multiple className="hidden" onChange={(e) => addReplyFiles(Array.from(e.target.files ?? []))} />
            <Button type="submit" size="sm" loading={postMessage.isPending} disabled={!replyContent.trim() && replyFiles.length === 0}>Wyślij</Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => { setReplyOpen(false); setReplyFiles([]); }}>Anuluj</Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Main conversation ─────────────────────────────────────────────────────────
interface Props {
  tenderId: string;
  messages: TenderMessage[];
  /** From ?message=<id> on a mention notification — that message is scrolled to and ringed. */
  highlightMessageId?: string | null;
  /** From ?tab=conversation — scroll the thread into view even without a message id. */
  focusConversation?: boolean;
}

export function TenderConversation({ tenderId, messages, highlightMessageId, focusConversation }: Props) {
  const [content, setContent] = useState("");
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const postMessage = usePostTenderMessage(tenderId);
  const { data: rawUsers = [] } = useInternalUsers();
  const users = rawUsers.map((u) => ({ id: u.id, full_name: u.full_name }));
  const mentionSuggestions = users.map((u) => ({ id: u.id, name: u.full_name }));

  const addFiles = useCallback((newFiles: File[]) => {
    setPendingFiles((prev) => [
      ...prev,
      ...newFiles.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
      })),
    ]);
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) addFiles(files);
  }, [addFiles]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!content.trim() && pendingFiles.length === 0) return;
    await postMessage.mutateAsync({
      content,
      files: pendingFiles.map((f) => f.file),
      mentionIds,
    });
    setContent("");
    setMentionIds([]);
    setPendingFiles([]);
  };

  const canSend = content.trim().length > 0 || pendingFiles.length > 0;

  // A mention notification links to ?tab=conversation (+ ?message=<id>). When only the
  // tab is given — the message was deleted, or the notification predates the id — land
  // on the thread header instead of the top of the tender.
  const headerRef = useRef<HTMLDivElement>(null);
  const hasHighlightTarget =
    !!highlightMessageId && collectMessageIds(messages).has(highlightMessageId);
  useEffect(() => {
    if (focusConversation && !hasHighlightTarget) {
      headerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusConversation, hasHighlightTarget]);

  return (
    <div>
      <div ref={headerRef} className="flex items-center gap-2 mb-4 scroll-mt-24">
        <MessageSquare size={16} className="text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Konwersacja ({messages.length})
        </h3>
      </div>

      <div className="space-y-3 mb-4">
        {messages.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">Brak wiadomości. Napisz pierwszą.</p>
        ) : (
          messages.map((msg) => (
            <MessageItem
              key={msg.id}
              tenderId={tenderId}
              message={msg}
              users={users}
              highlightMessageId={highlightMessageId}
            />
          ))
        )}
      </div>

      {/* Compose area */}
      <form onSubmit={handleSubmit}>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={isDragging ? "ring-2 ring-[var(--color-primary)] rounded" : ""}
        >
          {pendingFiles.length > 0 && (
            <div className="mb-2">
              <PendingFilesList
                files={pendingFiles}
                onRemove={(id) => setPendingFiles((p) => p.filter((f) => f.id !== id))}
              />
            </div>
          )}

          <RichTextEditor
            value={content}
            onChange={setContent}
            onMentionIdsChange={setMentionIds}
            onImageUpload={(file) => uploadTenderMessageImage(tenderId, file)}
            mentionSuggestions={mentionSuggestions}
            disabled={postMessage.isPending}
            placeholder={isDragging ? "Upuść pliki tutaj..." : "Napisz wiadomość…"}
          />
        </div>

        <div className="flex items-center justify-between gap-2 mt-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            title="Dodaj plik / zdjęcie"
          >
            <Paperclip size={14} />
            Załącz
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
            className="hidden"
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
          <Button
            type="submit"
            size="sm"
            loading={postMessage.isPending}
            disabled={!canSend}
          >
            Wyślij
          </Button>
        </div>
      </form>
    </div>
  );
}
